import { ArrowLeft, Calendar, ExternalLink, Tag } from 'lucide-react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useExamples, useNews } from '../hooks/useContent';
import { formatDate, getNewsUrl, getRelativeTime } from '../lib/content';
import { audienceLabels, complexityLabels, goalLabels, topicLabels } from '../lib/router';

export function TagPage() {
  const { slug } = useParams<{ slug: string }>();
  const { posts } = useNews();
  const { examples } = useExamples();
  
  const [activeTab, setActiveTab] = useState<'news' | 'examples'>(posts.length > 0 ? 'news' : 'examples');

  // Filter content by tag
  const filteredNews = posts.filter(post => 
    post.topics.includes(slug || '')
  );
  
  const filteredExamples = examples.filter(example => 
    example.tags?.includes(slug || '') || 
    example.audience === slug || 
    example.complexity === slug || 
    example.goal === slug
  );

  const tagLabel = topicLabels[slug || ''] || 
                  audienceLabels[slug || ''] || 
                  complexityLabels[slug || ''] || 
                  goalLabels[slug || ''] || 
                  (slug || '').replace('-', ' ');

  if (!slug) {
    return (
      <div className="container py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Tag Not Found</h1>
        <p className="text-muted-foreground">Please specify a valid tag.</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{tagLabel} - Promptliteracy.ai</title>
        <meta name="description" content={`Content tagged with ${tagLabel} - news articles and examples about AI literacy and prompt engineering.`} />
        <meta property="og:title" content={`${tagLabel} - Promptliteracy.ai`} />
        <meta property="og:description" content={`Content tagged with ${tagLabel} - news articles and examples about AI literacy and prompt engineering.`} />
      </Helmet>

      {/* Header Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container text-center">
          <nav className="mb-6 text-sm opacity-90">
            <Link to="/" className="link-underline text-white/80">
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link to="/tags" className="link-underline text-white/80">
              Tags
            </Link>
            <span className="mx-2">/</span>
            <span>{tagLabel}</span>
          </nav>
          
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Tag className="h-6 w-6 sm:h-8 sm:w-8" />
              <h1 className="text-2xl sm:text-4xl font-bold">{tagLabel}</h1>
            </div>
            <p className="text-lg sm:text-xl opacity-90 mb-6">
              Explore {filteredNews.length} articles and {filteredExamples.length} examples related to {tagLabel.toLowerCase()}
            </p>
            
            {/* Tab Navigation */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                onClick={() => setActiveTab('news')}
                variant="ghost"
                className={`px-4 sm:px-8 py-3 sm:py-4 font-semibold transition-all rounded-xl shadow-lg ${
                  activeTab === 'news' 
                    ? 'bg-white text-slate-600 hover:bg-white/90' 
                    : 'bg-white text-primary border-2 border-primary/20 hover:border-primary hover:bg-primary/10 hover-lift'
                }`}
              >
                News ({filteredNews.length})
              </Button>
              <Button
                onClick={() => setActiveTab('examples')}
                variant="ghost"
                className={`px-4 sm:px-8 py-3 sm:py-4 font-semibold transition-all rounded-xl shadow-lg ${
                  activeTab === 'examples' 
                    ? 'bg-white text-slate-600 hover:bg-white/90' 
                    : 'bg-white text-primary border-2 border-primary/20 hover:border-primary hover:bg-primary/10 hover-lift'
                }`}
              >
                Examples ({filteredExamples.length})
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-8">
        {/* News Tab */}
        {activeTab === 'news' && (
          <div className="space-y-6">
            {filteredNews.length > 0 ? (
              filteredNews.map((post) => (
                <Link key={post.slug} to={getNewsUrl(post)} className="block group">
                  <Card className="card-slate hover:shadow-lg transition-all hover:scale-[1.01] border-l-4 border-l-primary/30 hover:border-l-primary cursor-pointer card-accent">
                  <CardHeader>
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2 leading-tight text-primary">
                          {post.title}
                        </CardTitle>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="font-medium">{post.source_name}</span>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(post.published_at)}</span>
                          </div>
                          <span>•</span>
                          <span>{getRelativeTime(post.published_at)}</span>
                        </div>
                      </div>
                      <a
                        href={post.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        title="Read original article"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-5 w-5" />
                      </a>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      {post.dek}
                    </p>
                    
                    {post.curator_note && (
                      <div className="bg-accent/50 border-l-4 border-primary pl-4 py-2 mb-4">
                        <p className="text-sm italic">
                          <span className="font-medium">Editor's note:</span> {post.curator_note}
                        </p>
                      </div>
                    )}

                    <div className="mb-4">
                      {post.topics.map((topic, index) => (
                        <span key={topic}>
                          <Link 
                            to={`/tag/${topic}`}
                            className={`text-xs link-underline cursor-pointer transition-colors ${
                              topic === slug 
                                ? "text-primary font-medium" 
                                : "text-secondary hover:text-primary"
                            }`}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            {topicLabels[topic] || topic.replace('-', ' ')}
                          </Link>
                          {index < post.topics.length - 1 && (
                            <span className="text-muted-foreground mx-2">|</span>
                          )}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-border/40">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-slate-300 btn-blue-hover transition-colors"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          window.open(post.source_url, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        Read Original Article
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                </Link>
              ))
            ) : (
              <div className="text-center py-16">
                <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No news articles found</h3>
                <p className="text-muted-foreground mb-4">
                  There are no news articles tagged with "{tagLabel}"
                </p>
                <Button variant="outline" asChild className="border-slate-300 btn-blue-hover transition-colors">
                  <Link to="/news">Browse All News</Link>
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Examples Tab */}
        {activeTab === 'examples' && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredExamples.length > 0 ? (
              filteredExamples.map((example) => (
                <Link key={example.slug} to={`/examples/${example.slug}`} className="block group">
                  <Card className="card-slate hover-lift border-t-4 border-t-primary/30 hover:border-t-primary transition-all hover:scale-105 cursor-pointer card-accent">
                  <CardHeader>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Link to={`/tag/${example.complexity}`} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Badge variant="outline" className="text-xs bg-primary/10 border-primary/30 text-primary hover:text-primary link-underline cursor-pointer">
                          {complexityLabels[example.complexity]}
                        </Badge>
                      </Link>
                      <Link to={`/tag/${example.audience}`} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Badge variant="secondary" className="text-xs bg-secondary/10 border-secondary/30 text-secondary hover:text-primary link-underline cursor-pointer">
                          {audienceLabels[example.audience]}
                        </Badge>
                      </Link>
                      <Link to={`/tag/${example.goal}`} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Badge variant="default" className="text-xs bg-primary text-white hover:text-white link-underline cursor-pointer">
                          {goalLabels[example.goal]}
                        </Badge>
                      </Link>
                    </div>
                    <CardTitle className="text-lg leading-tight text-primary">{example.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {example.context}
                    </p>
                    
                    {example.tags && example.tags.length > 0 && (
                      <div className="mb-4">
                        {example.tags.map((tag, index) => (
                          <span key={tag}>
                            <Link 
                              to={`/tag/${tag}`}
                              className={`text-xs link-underline cursor-pointer transition-colors ${
                                tag === slug 
                                  ? "text-primary font-medium" 
                                  : "text-secondary hover:text-primary"
                              }`}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              {topicLabels[tag] || tag.replace('-', ' ')}
                            </Link>
                            {index < example.tags.length - 1 && (
                              <span className="text-muted-foreground mx-2">|</span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full border-slate-300 btn-blue-hover transition-colors"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        window.location.href = `/examples/${example.slug}`;
                      }}
                    >
                      View Example
                    </Button>
                  </CardContent>
                </Card>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-16">
                <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No examples found</h3>
                <p className="text-muted-foreground mb-4">
                  There are no examples tagged with "{tagLabel}"
                </p>
                <Button variant="outline" asChild className="border-slate-300 btn-blue-hover transition-colors">
                  <Link to="/examples">Browse All Examples</Link>
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Back Navigation */}
        <div className="mt-12 pt-8 border-t border-border/40 text-center">
          <Button variant="outline" asChild className="border-slate-300 btn-blue-hover transition-colors">
            <Link to="/tags">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Browse All Tags
            </Link>
          </Button>
        </div>
      </div>
    </>
  );
}